import { Drawer, Form, message, Row, Spin, Typography } from 'antd';
import IssueForm from '../issueForm';
import CauseForm from '../causeForm';

import {
  Cause,
  Issue,
  QueryType,
  Routine,
  SuggestionStatus,
} from '../../types';
import { useEffect, useRef, useState } from 'react';
import {
  reviewSuggestionCause,
  reviewSuggestionIssue,
  reviewSuggestionRoutine,
} from '../../utils/httpRequest';
import { useSelector } from 'react-redux';
import { selectUserId, selectUsername } from '../../slices/profile';
import SuggestionReviewDrawerHeader from '../reviewDrawerHeader';
import { suggestionReviewTitles } from '../../constants';
import { useAppDispatch } from '../../store';
import { fetchNotifications } from '../../slices/notifications';

interface SuggestionDrawerProps {
  queryType: QueryType;
  visible: boolean;
  onClose: (isSubmitted?: boolean) => void;
  data: Issue | Cause | Routine;
}

const SuggestionReviewDrawer = ({
  queryType,
  visible,
  onClose,
  data,
}: SuggestionDrawerProps) => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [reviewResult, setReviewResult] = useState('' as SuggestionStatus);
  const [modalVisible, setModalVisible] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const userId = useSelector(selectUserId);
  const username = useSelector(selectUsername);
  const title = suggestionReviewTitles[queryType];
  const formData = useRef<Issue | Cause>(null);

  useEffect(() => {
    form.setFieldsValue(data);
  }, [form, data]);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      formData.current = null;
    }
  }, [visible]);

  const handleReview = async (
    action: SuggestionStatus,
    description?: string
  ) => {
    try {
      setLoading(true);
      await form.validateFields();
      const newData = {
        ...data,
        ...formData.current,
        ...form.getFieldsValue(),
        suggestionStatus: action,
        suggestionReviewerID: userId,
        suggestionReviewerName: username,
        suggestionReviewDescription: description,
      };
      switch (queryType) {
        case QueryType.ISSUE:
          await reviewSuggestionIssue(newData);
          break;
        case QueryType.CASUE:
          await reviewSuggestionCause(newData);
          break;
        default:
          break;
      }
      form.resetFields();
      onClose(true);
      dispatch(fetchNotifications());
    } catch (err) {
      messageApi.error('Submit failed, please try again!');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = () => {
    setReviewResult(SuggestionStatus.REJECTED);
    setModalVisible(true);
  };

  const handleApprove = () => {
    setReviewResult(SuggestionStatus.APPROVED);
    setModalVisible(true);
  };

  const onConfirm = () => {
    handleReview(
      reviewResult,
      form.getFieldValue('suggestionReviewDescription')
    );
    setModalVisible(false);
  };

  const renderForm = () => {
    switch (queryType) {
      case QueryType.ISSUE:
        return (
          <IssueForm
            templateID={String(data.templateID)}
            form={form}
            issue={data as Issue}
            isSuggestion
            isSuggestionReview
            onChange={(issue) => (formData.current = issue)}
          />
        );

      case QueryType.CASUE:
        return (
          <CauseForm
            templateID={String(data.templateID)}
            form={form}
            cause={data as Cause}
            isSuggestion
            isSuggestionReview
            onChange={(cause) => (formData.current = cause)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Drawer
      title={
        <>
          <SuggestionReviewDrawerHeader
            title={title}
            name={data.name}
            onReject={handleReject}
            onApprove={handleApprove}
            reviewResult={reviewResult}
            form={form}
            onConfirm={onConfirm}
            modalVisible={modalVisible}
            onModalClose={() => {
              setModalVisible(false);
              form.resetFields();
            }}
          />
          <Typography.Text type="secondary" style={{ fontWeight: 400 }}>
            Submit By: {data?.suggestionSubmitterName || '-'} {'    '} at{' '}
            {data?.suggestionSubmittedTime?.replace('T', ' ') || '-'}
          </Typography.Text>
        </>
      }
      closable
      onClose={() => onClose()}
      open={visible}
      width={800}
    >
      {contextHolder}
      <Spin fullscreen spinning={loading}></Spin>

      <Form form={form} layout="vertical">
        {renderForm()}
      </Form>
    </Drawer>
  );
};

export default SuggestionReviewDrawer;
